import { GuideFaq } from '../models/guide.model';

/**
 * Static FAQ knowledge base (MVP).
 *
 * Seeded from the brand-facing shoutugc FAQ bank (excludes "For Creators").
 * In a later pass this can be served from `GET /guide/faqs`. Kept as a
 * type-checked TS constant for now so the classifier / matcher can match
 * via `triggerPhrases` the same way guide tasks do.
 */
export const GUIDE_FAQS: GuideFaq[] = [
  // —— General ——
  {
    id: 'what-is-shout',
    category: 'General',
    question: 'What is SHOUT?',
    answer:
      'SHOUT is a self-service UGC platform that connects brands with creators to generate authentic user-generated content at scale.',
    triggerPhrases: [
      'what is shout',
      'what is shout ugc',
      'tell me about shout',
      'explain shout',
      'what does shout do',
    ],
    isFeatured: false,
  },
  {
    id: 'get-started-with-shout',
    category: 'General',
    question: 'How do I get started with SHOUT?',
    answer:
      "If you're a brand or agency, simply sign up on our website (app.shoutugc.com) and follow the steps to create an account and start using the platform. If you're a Creator please download the SHOUT app on iOS or Android.",
    triggerPhrases: [
      'how do i get started with shout',
      'get started with shout',
      'how do i sign up',
      'how to start using shout',
    ],
    isFeatured: false,
  },
  {
    id: 'content-quality',
    category: 'General',
    question: 'How do brands ensure the quality of the content?',
    answer:
      'All our creators are verified members of the SHOUT community, and we have a review process in place to ensure the quality and authenticity of the content.',
    triggerPhrases: [
      'how do brands ensure the quality of the content',
      'content quality',
      'how is content quality ensured',
      'quality of content',
    ],
    isFeatured: false,
  },
  {
    id: 'content-types',
    category: 'General',
    question: 'What kind of content can creators produce?',
    answer:
      "A wide range of video content, including unboxings, reviews, complete product overviews, as well as custom videos tailored to the brand's requirements.",
    triggerPhrases: [
      'what kind of content can creators produce',
      'what content can creators make',
      'types of content',
      'what videos can creators create',
    ],
    isFeatured: false,
  },
  {
    id: 'choose-creators',
    category: 'General',
    question: 'Can I choose what creators I work with?',
    answer:
      'Yes, you have complete control. Once you post a brief you can either search and invite creators to apply, or wait for creators to apply. Then you choose who you want to hire.',
    triggerPhrases: [
      'can i choose what creators i work with',
      'choose creators',
      'pick which creators',
      'select creators to hire',
    ],
    isFeatured: true,
  },
  {
    id: 'creators-vetted',
    category: 'General',
    question: 'Are the influencers vetted or verified?',
    answer:
      'Yes, all creators on our platform go through a vetting process to ensure content quality, real engagement, and professional delivery.',
    triggerPhrases: [
      'are the influencers vetted or verified',
      'are creators vetted',
      'are influencers verified',
      'creator verification',
    ],
    isFeatured: true,
  },
  {
    id: 'agencies-use-platform',
    category: 'General',
    question: 'Can agencies use the platform for their clients?',
    answer:
      'Absolutely. Our platform supports multiple brand profiles and wallets, making it easy for agencies to manage campaigns for different clients under one login.',
    triggerPhrases: [
      'can agencies use the platform for their clients',
      'agencies use the platform',
      'agency accounts',
      'manage multiple clients',
    ],
    isFeatured: false,
  },
  {
    id: 'usage-rights',
    category: 'General',
    question: 'What are the usage rights for the UGC content I receive?',
    answer:
      'All content created through SHOUT comes with perpetual, worldwide usage rights. This means you can use the content across all your marketing channels, including paid ads, social media, websites, and email campaigns, without additional licensing fees.',
    triggerPhrases: [
      'what are the usage rights for the ugc content i receive',
      'usage rights',
      'content licensing',
      'can i use the content in ads',
      'perpetual usage rights',
    ],
    isFeatured: true,
  },
  {
    id: 'request-revisions',
    category: 'General',
    question: 'Can I request revisions on the content?',
    answer:
      "You can request unlimited revisions. If you're still not happy, un-hire the creator and your funds are returned to your wallet.",
    triggerPhrases: [
      'can i request revisions on the content',
      'request revisions',
      'content revisions',
      'revise content',
    ],
    isFeatured: true,
  },

  // —— Pricing & Payments ——
  {
    id: 'membership-how-it-works',
    category: 'Pricing & Payments',
    question: 'How does the membership work?',
    answer:
      'With a monthly membership, you get full access to our platform where you can connect with vetted UGC creators. Post briefs, hire creators, and pay only when you approve the content.',
    triggerPhrases: [
      'how does the membership work',
      'how does membership work',
      'membership',
      'subscription how it works',
    ],
    isFeatured: true,
  },
  {
    id: 'pay-per-content',
    category: 'Pricing & Payments',
    question: 'Do I have to pay for every piece of content?',
    answer:
      "Yes, but only for content you approve. You'll receive submissions from creators you hire and funds are only released once you accept the content.",
    triggerPhrases: [
      'do i have to pay for every piece of content',
      'pay for every piece of content',
      'when do i pay for content',
      'pay only when approved',
    ],
    isFeatured: true,
  },
  {
    id: 'what-is-shout-wallet',
    category: 'Pricing & Payments',
    question: 'What is my SHOUT Wallet?',
    answer:
      'Your SHOUT Wallet holds funds for creator payments. Top up anytime — funds stay in escrow until you approve content.',
    triggerPhrases: [
      'what is my shout wallet',
      'what is shout wallet',
      'what is my wallet',
      'how does the wallet work',
      'wallet escrow',
    ],
    isFeatured: true,
  },
  {
    id: 'dont-like-content',
    category: 'Pricing & Payments',
    question: "What if I don't like the content?",
    answer:
      "You can request unlimited revisions. If you're still not happy, un-hire the creator and your funds are returned to your wallet.",
    triggerPhrases: [
      "what if i don't like the content",
      'what if i dont like the content',
      'unhappy with content',
      'refund if i dont like content',
    ],
    isFeatured: false,
  },
  {
    id: 'cancel-membership',
    category: 'Pricing & Payments',
    question: 'Can I cancel my membership at any time?',
    answer:
      'Yes. You can cancel anytime and continue to use the platform until your current billing cycle ends.',
    triggerPhrases: [
      'can i cancel my membership at any time',
      'cancel membership',
      'cancel subscription',
      'how do i cancel',
    ],
    isFeatured: false,
  },
  {
    id: 'hidden-fees',
    category: 'Pricing & Payments',
    question: 'Are there any hidden fees?',
    answer:
      "No hidden fees. You pay a transparent membership fee and per-content pricing that's clearly shown before checkout.",
    triggerPhrases: [
      'are there any hidden fees',
      'hidden fees',
      'extra fees',
      'are there additional fees',
    ],
    isFeatured: false,
  },
  {
    id: 'minimum-spend',
    category: 'Pricing & Payments',
    question: 'Is there a minimum spend to use the platform?',
    answer: 'The minimum top up amount into your wallet is £100 or $100.',
    triggerPhrases: [
      'is there a minimum spend to use the platform',
      'minimum spend',
      'minimum top up',
      'minimum wallet top up',
    ],
    isFeatured: false,
  },
  {
    id: 'how-creators-get-paid',
    category: 'Pricing & Payments',
    question: 'How do creators get paid?',
    answer:
      "Creators are paid for their content once the brand approves it. Payments are processed through the SHOUT platform straight into Creators' bank account.",
    triggerPhrases: [
      'how do creators get paid',
      'creator payments',
      'when are creators paid',
      'how creators are paid',
    ],
    isFeatured: false,
  },

  // —— Hiring Influencers ——
  {
    id: 'hire-influencers',
    category: 'Hiring Influencers',
    question: 'How do I hire influencers through the platform?',
    answer:
      'Simply post a brief outlining your campaign goals and budget. Within minutes, verified influencers will apply, and you can review their profiles, content, and rates before hiring.',
    triggerPhrases: [
      'how do i hire influencers through the platform',
      'hire influencers',
      'how do i hire influencers',
      'hiring influencers on shout',
    ],
    isFeatured: true,
  },
  {
    id: 'micro-influencer-cost',
    category: 'Hiring Influencers',
    question: 'How much does it cost to work with micro influencers?',
    answer:
      'Rates vary depending on the creator, but our platform offers affordable pricing starting from $169 per video. You set your budget and only pay when you hire a creator.',
    triggerPhrases: [
      'how much does it cost to work with micro influencers',
      'micro influencer cost',
      'cost of micro influencers',
      'micro influencer pricing',
    ],
    isFeatured: true,
  },
  {
    id: 'influencer-post-cost',
    category: 'Hiring Influencers',
    question: 'How much do influencer posts cost?',
    answer:
      'There is a base price of £100 for UK Creators and $160 for US Creators. There is then an extra cost depending on the number of followers. For example a UK Creator with 9K followers will cost £120 and with 90K followers will cost £400.',
    triggerPhrases: [
      'how much do influencer posts cost',
      'influencer post cost',
      'influencer pricing',
      'cost of influencer posts',
    ],
    isFeatured: false,
  },
  {
    id: 'track-campaign-performance',
    category: 'Hiring Influencers',
    question: 'Can I track performance of influencer campaigns?',
    answer:
      'Yes, our built-in dashboard lets you monitor submissions, engagement metrics, and view results in real time so you can measure ROI and content performance easily.',
    triggerPhrases: [
      'can i track performance of influencer campaigns',
      'track campaign performance',
      'influencer campaign metrics',
      'measure campaign roi',
    ],
    isFeatured: true,
  },
  {
    id: 'influencer-platforms',
    category: 'Hiring Influencers',
    question: 'What platforms do your influencers post on?',
    answer:
      'Our creators are active on TikTok, Instagram, YouTube, and Snapchat. You can choose where you want your campaign content to appear based on your goals.',
    triggerPhrases: [
      'what platforms do your influencers post on',
      'which platforms do influencers use',
      'influencer platforms',
      'tiktok instagram youtube snapchat',
    ],
    isFeatured: false,
  },
  {
    id: 'content-turnaround',
    category: 'Hiring Influencers',
    question: 'How quickly can I get content from creators?',
    answer:
      'Most creators deliver within 3 days of being hired. You can filter by turnaround time and select creators who meet your delivery needs.',
    triggerPhrases: [
      'how quickly can i get content from creators',
      'content turnaround',
      'how fast do creators deliver',
      'delivery time for content',
    ],
    isFeatured: false,
  },
  {
    id: 'boost-content-with-ads',
    category: 'Hiring Influencers',
    question: "Can I boost the content with my ad budget after it's posted?",
    answer:
      'Yes, the Creator will provide you with the Ad Code, and with this you can boost the video to your target audience.',
    triggerPhrases: [
      'can i boost the content with my ad budget after its posted',
      'boost content with ads',
      'ad code',
      'boost the video',
    ],
    isFeatured: false,
  },

  // —— Nano Influencers ——
  {
    id: 'why-nano-influencers',
    category: 'Nano Influencers',
    question: 'Why should brands collaborate with nano influencers?',
    answer:
      'Nano influencers deliver higher engagement rates, genuine authenticity, and cost-efficiency compared to larger influencers. They excel at fostering trust and sparking meaningful conversations around your products or services.',
    triggerPhrases: [
      'why should brands collaborate with nano influencers',
      'why nano influencers',
      'benefits of nano influencers',
      'why work with nano influencers',
    ],
    isFeatured: false,
  },
  {
    id: 'brands-nano-influencers',
    category: 'Nano Influencers',
    question: 'Which brands benefit most from nano influencer marketing?',
    answer:
      'Beauty, fashion, lifestyle, fitness, technology, and niche brands benefit the most—especially those targeting specific demographics or fostering community engagement.',
    triggerPhrases: [
      'which brands benefit most from nano influencer marketing',
      'brands that benefit from nano influencers',
      'who should use nano influencers',
    ],
    isFeatured: false,
  },
  {
    id: 'nano-influencer-content',
    category: 'Nano Influencers',
    question: 'What kind of content do nano influencers create?',
    answer:
      'Nano influencers create user-generated content (UGC) like social media posts, videos, reviews, and stories—showcasing your products in a relatable and authentic way that resonates with their audience.',
    triggerPhrases: [
      'what kind of content do nano influencers create',
      'nano influencer content',
      'what do nano influencers create',
    ],
    isFeatured: false,
  },
  {
    id: 'nano-for-small-business',
    category: 'Nano Influencers',
    question: 'Is nano influencer marketing suitable for small businesses?',
    answer:
      'Absolutely! Nano influencer campaigns are cost-effective and perfect for small businesses looking to target specific audiences with precision.',
    triggerPhrases: [
      'is nano influencer marketing suitable for small businesses',
      'nano influencers for small businesses',
      'small business nano influencers',
    ],
    isFeatured: false,
  },

  // —— TikTok ——
  {
    id: 'match-tiktok-creators',
    category: 'TikTok',
    question: 'How do you match brands with creators on TikTok?',
    answer:
      "At SHOUT UGC, we match brands with TikTok creators by leveraging our extensive network of TikTok UGC experts. We carefully evaluate creators to ensure they align with your brand's voice, target audience, and campaign objectives. Our process prioritises creators who excel in producing authentic and engaging user-generated content (UGC) that drives impact on TikTok's fast-paced platform.",
    triggerPhrases: [
      'how do you match brands with creators on tiktok',
      'match brands with tiktok creators',
      'tiktok creator matching',
    ],
    isFeatured: false,
  },
  {
    id: 'measure-tiktok-success',
    category: 'TikTok',
    question: 'How do I measure the success of TikTok UGC campaigns?',
    answer:
      'Measuring the success of TikTok UGC campaigns involves tracking key metrics such as engagement rates, video views, likes, comments, and shares. At SHOUT UGC, we also provide insights into follower growth, click-through rates, and conversions directly tied to the user-generated content.',
    triggerPhrases: [
      'how do i measure the success of tiktok ugc campaigns',
      'measure tiktok ugc success',
      'tiktok campaign metrics',
      'tiktok ugc performance',
    ],
    isFeatured: true,
  },
  {
    id: 'tiktok-roi',
    category: 'TikTok',
    question: 'What ROI can I expect from a TikTok UGC campaign?',
    answer:
      'The ROI from a TikTok UGC campaign depends on your specific objectives, audience, and investment. SHOUT UGC specialises in creating relatable and authentic UGC TikTok content that drives high engagement, brand awareness, and trust. With our proven strategies, brands often see significant boosts in visibility, audience interaction, and conversions.',
    triggerPhrases: [
      'what roi can i expect from a tiktok ugc campaign',
      'tiktok ugc roi',
      'tiktok campaign roi',
      'return on investment tiktok',
    ],
    isFeatured: false,
  },
  {
    id: 'tiktok-results-timeline',
    category: 'TikTok',
    question: 'How long does it take to see results from TikTok UGC?',
    answer:
      'UGC TikTok campaigns often deliver engagement, such as views and interactions, within hours or days of posting. However, achieving sustained results like increased brand awareness or consistent conversions can take several weeks to months of strategic UGC content.',
    triggerPhrases: [
      'how long does it take to see results from tiktok ugc',
      'tiktok ugc results timeline',
      'when will i see tiktok results',
      'how fast do tiktok campaigns work',
    ],
    isFeatured: true,
  },
];
